import React, { useEffect, useState, useCallback } from "react";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import style from "./style.module.css";
import TruncatedText from "../../Components/TruncatedText";
import { API_BASE } from "../../config/api";
import Pagination from "../../Components/Pagination";

const PAGE_SIZE = 25;

interface ReviewRecord {
  review_id: number;
  user_id: number;
  product_id: number;
  rating: number;
  comment: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
  product_name?: string;
}

const ReviewPage: React.FC = () => {
  const [cookies] = useCookies();
  const [reviews, setReviews] = useState<ReviewRecord[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Debounce the search box so it doesn't refetch on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // A new search term invalidates the current page — go back to page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchReviews = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await authenticatedFetch(`${API_BASE}/review/getall?${params}`, { method: "GET" }, cookies.auth);
      if (res.ok) {
        const result = await res.json();
        setReviews(Array.isArray(result.data) ? result.data : []);
        setTotalPages(result.totalPages || 1);
        setTotalItems(result.total || 0);
      }
    } catch (e) {
      console.error("Failed to fetch reviews", e);
    } finally {
      setIsLoading(false);
    }
  }, [cookies.auth, page, debouncedSearch]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  return (
    <div className={style.wrapper}>
      <div className={style.header}>
        <h1 className={style.title}>Review</h1>
        <div className={style.searchSection}>
          <input
            type="text"
            placeholder="Search review comments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={style.searchInput}
          />
        </div>
      </div>

      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <colgroup>
            <col style={{ width: '110px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '200px' }} />
            <col style={{ width: '90px' }} />
            <col />
            <col style={{ width: '140px' }} />
            <col style={{ width: '140px' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Review ID</th>
              <th>User</th>
              <th>Product</th>
              <th>Rating</th>
              <th>Comment</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {reviews.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                  {isLoading ? "Loading reviews..." : "No reviews found."}
                </td>
              </tr>
            ) : (
              reviews.map(r => (
                <tr key={r.review_id}>
                  <td>{r.review_id}</td>
                  <td>{r.user_name || `User ${r.user_id}`}</td>
                  <td>{r.product_name || `Product ${r.product_id}`}</td>
                  <td>{r.rating}</td>
                  <td className={style.descriptionCell}>
                    <TruncatedText text={r.comment || ''} maxLength={40} />
                  </td>
                  <td>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                  <td>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : ""}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />
    </div>
  );
};

export default ReviewPage;

